import React from 'react';
import TrustpilotCarousel from './TrustpilotCarousel';

/**
 * Step content shell. Title + content slots only.
 *
 * Iter-6 (operator walk 2026-05-09): the legacy chrome that this
 * component used to render — top progress bar, top-left "X / Y"
 * counter, and bottom-right nav arrows — has been removed:
 *   - progress bar: superseded by `<MoneyProgressBar>` at App.jsx level
 *     (iter-3 / PR #214 was supposed to do this replacement but only
 *     did the addition; iter-6 finishes the swap).
 *   - counter: forbidden by the youform-mirror plan ("does not show
 *     step counts").
 *   - nav arrows: extracted into `<TakeoverNav>` and hoisted to
 *     App.jsx as a sibling of `.step-transition`. Inside the wrapper
 *     they were broken because the step-transition keyframe applies
 *     `transform: translateY(...)` with `animation-fill-mode: both`,
 *     which makes any descendant with `position: fixed` resolve its
 *     containing block against the transformed ancestor instead of
 *     the viewport — the arrows visibly drifted to mid-content.
 */
// iter-23: when a step passes a string title like `"Which country are you
// from? *"`, detect the trailing required-asterisk and wrap it in a span
// styled by the global `.required-asterisk-inline` rule. Without this, the
// asterisk renders at the full 48px h1 size and wraps to its own line on
// long titles, which the operator flagged on the Country page screenshot.
//
// Steps that need full custom styling on the asterisk can still pass JSX
// for the title (e.g. Step05LinkedInCredentials) — the JSX path bypasses
// this regex split.
function renderTitle(title) {
  if (typeof title !== 'string') return title;
  const match = title.match(/^(.*?)(\s*\*)\s*$/);
  if (!match) return title;
  const [, head, ast] = match;
  return (
    <>
      {head}
      <span className="required-asterisk-inline">{ast.trim() === '' ? '*' : ast.replace(/^\s+/, ' ')}</span>
    </>
  );
}

export default function StepLayout({
  title,
  children,
  // Visual variants — opt-in per step:
  //   "centered"          → center heading + content (welcome / modal-style)
  //   "underline-fields"  → underline-only inputs + bold labels (forms)
  variant,
  // iter-24: TrustpilotCarousel renders below the step's children on
  // every page by default (operator directive: "trustpilot should now
  // be on every single page"). Steps that don't want it (e.g. wait
  // screens that should feel undisturbed) can opt out with
  // `hideTrustpilot`. The carousel previously had to be added by every
  // step file individually; pulling it into StepLayout means new steps
  // get it automatically without each one needing to import it.
  hideTrustpilot = false,
}) {
  const variantClass = variant ? `step-layout--${variant}` : '';
  return (
    <div className={`step-layout ${variantClass}`.trim()}>
      {title && <h1 className="step-title">{renderTitle(title)}</h1>}
      <div className="step-content">{children}</div>
      {!hideTrustpilot && <TrustpilotCarousel />}
    </div>
  );
}
